import { Component, computed, inject, input, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_FORMFIELD } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_CARD } from '@progress/kendo-angular-layout';
import { UploadsModule } from '@progress/kendo-angular-upload';
import { AbstractNewComponent } from '@unity/abstract/abstract-new.component';
import { AgreementsClient, CreatedResponseDto, ReferenceFileCreateDto, ReferenceFilesClient } from '@unity/clients';
import { FileUploaderControlComponent } from '@unity/forms/file-uploader-control/file-uploader-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardNewSharedComponent } from '@unity/shared/composed/card-new-shared/card-new-shared.component';
import { nonZeroFormControl, stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityRouteUrlTrees } from '@unity/utils/types';
import { EMPTY } from 'rxjs';

export class ReferenceFileCreateSchema implements FormGroupControlSchema<ReferenceFileCreateDto> {
  public agreement_id = nonZeroFormControl();
  public name = stringFormControl();
  public upload_id = nonZeroFormControl();

  public constructor(agreementId: number) {
    this.agreement_id = nonZeroFormControl({ value: agreementId });
  }
}

@Component({
  selector: 'unity-new-reference-file-page',
  standalone: true,
  imports: [
    KENDO_CARD,
    KENDO_LOADER,
    KENDO_LABEL,
    KENDO_FORMFIELD,
    ReactiveFormsModule,
    UploadsModule,
    TextboxControlComponent,
    FileUploaderControlComponent,
    PageContainerComponent,
    CardNewSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './new-reference-file-page.component.html',
  styleUrl: './new-reference-file-page.component.scss',
})
export class NewReferenceFilePageComponent extends AbstractNewComponent<
  ReferenceFileCreateDto,
  ReferenceFileCreateSchema,
  CreatedResponseDto
> {
  private readonly agreementsClient = inject(AgreementsClient);
  private readonly referenceFilesClient = inject(ReferenceFilesClient);

  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });

  protected override readonly $contentHeader = signal('Create New Reference File');
  protected override createResourceFn$ = (form: FormGroup<ReferenceFileCreateSchema>) => {
    const agreementId = this.$agreementId();
    if (agreementId) {
      return this.referenceFilesClient.createOneForAgreement(agreementId, form.getRawValue());
    } else {
      return EMPTY;
    }
  };

  protected override redirectUrlFn = (): UnityRouteUrlTrees => ['/agreement', this.$agreementId(), 'document'];
  protected override newControlsFn = (): ReferenceFileCreateSchema => {
    const agreementId = this.$agreementId();
    return new ReferenceFileCreateSchema(agreementId ?? 0);
  };

  protected agreementResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      return agreementId ? { agreementId } : undefined;
    },
    stream: ({ params: { agreementId } }) => this.agreementsClient.getOneAsMini(agreementId),
  });

  protected readonly $contentHeaderSecondary = computed(() => {
    const agreement = this.agreementResource.value();
    return `Agreement: ${agreement?.name}`;
  });

  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));
}
